import { router } from "@inertiajs/react"
import { PlugZap } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Whether the delivery platforms are actually working.
 *
 * **This screen leads with what is broken, not with what is connected.** That is
 * the hardest requirement in #321 and it is a UI problem rather than a backend
 * one: an order that never arrived is invisible by nature. Nothing appears, no
 * error is raised, and the restaurant finds out when a customer calls to ask
 * where their food is. A list of green logos would be an honest rendering of
 * `status` and a dishonest rendering of reality.
 *
 * So the count comes first, and it is printed even when it is zero — "0 canales
 * con problemas" is a statement somebody can trust, where an empty space is an
 * absence they have to interpret.
 *
 * `healthy` is `connected && !stale`, computed on the server. A channel that
 * says `connected` and has not synced in fifteen minutes reads as a problem
 * here, because that is what it is.
 */

export type Channel = {
  id: number
  platform: string
  platformLabel: string
  externalStoreId: string
  status: string
  healthy: boolean
  stale: boolean
  lastSyncedAt: string | null
  lastFailureAt: string | null
  lastFailureReason: string | null
  credentialConfigured: boolean
}

function tChannels(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.manage.channels.index.${key}`, options)
}

function ChannelRow({ channel, timeZone }: { channel: Channel; timeZone: string }) {
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`channel-${channel.platform}`}>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{channel.platformLabel}</span>
        <span className="text-muted-foreground block truncate text-xs">
          {channel.externalStoreId}
        </span>
      </span>

      <span className="flex items-center gap-2">
        <StatusBadge
          tone={channel.healthy ? "success" : "danger"}
          label={channel.healthy ? tChannels("healthy") : tChannels("unhealthy")}
          data-testid={`channel-health-${channel.platform}`}
        />

        {channel.status !== "disconnected" ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={tChannels("disconnect", { platform: channel.platformLabel })}
            data-testid={`channel-disconnect-${channel.platform}`}
            onClick={() =>
              router.patch(`/manage/channels/${channel.id}`, {}, { preserveScroll: true })
            }
          >
            <PlugZap className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </span>

      {/* The reason, when there is one. A failed integration that only shows a
          red dot tells an operator to call support; one that says "timeout"
          tells them to wait, and one that says "credential rejected" tells them
          what to fix. */}
      <span className="text-muted-foreground w-full text-xs tabular-nums">
        {channel.lastFailureReason
          ? tChannels("last_failure", {
              reason: channel.lastFailureReason,
              at: channel.lastFailureAt
                ? formatDateTime(channel.lastFailureAt, { timeZone })
                : tChannels("unknown_time"),
            })
          : channel.lastSyncedAt
            ? tChannels("last_sync", { at: formatDateTime(channel.lastSyncedAt, { timeZone }) })
            : tChannels("never_synced")}
      </span>
    </li>
  )
}

export function ChannelHealth({
  channels,
  unhealthyCount,
  unresolvedCount,
  timeZone,
}: {
  channels: Channel[]
  unhealthyCount: number
  unresolvedCount: number
  timeZone: string
}) {
  return (
    <Card density="compact" data-testid="channel-health">
      <CardHeader className="border-b">
        <CardTitle>{tChannels("title")}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Printed at zero too. "Todo en orden" is a claim; blank space is not. */}
        <p
          className="text-foreground text-sm font-semibold tabular-nums"
          data-testid="channel-unhealthy-count"
        >
          {unhealthyCount === 0
            ? tChannels("all_healthy")
            : tChannels("unhealthy_count", { count: unhealthyCount })}
        </p>

        {channels.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="channel-empty">
            {tChannels("empty")}
          </p>
        ) : (
          <ul className="divide-border divide-y rounded-md border" data-testid="channel-list">
            {channels.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} timeZone={timeZone} />
            ))}
          </ul>
        )}

        <div className="space-y-1">
          <SectionHeader title={tChannels("orders_title")} className="px-0" />
          {/* The disclosure that keeps two screens from disagreeing. The mix
              report deliberately excludes channel orders — none of them
              materializes an order line — and an owner comparing the two
              numbers deserves to know that before they go hunting for money
              that was never missing. */}
          <p className="text-muted-foreground text-xs" data-testid="channel-report-note">
            {tChannels("not_in_reports")}
          </p>
          {unresolvedCount > 0 ? (
            <p className="text-foreground text-sm tabular-nums" data-testid="channel-unresolved">
              {tChannels("unresolved", { count: unresolvedCount })}
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
